import { Phone, Mail, MapPin, Clock, Send } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { useState } from 'react';
import { trackFormSubmission } from '../utils/trackConversion';
import SEO from '../components/SEO';
import { breadcrumbSchema, seoKeywords } from '../utils/seoData';

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    location: '',
    message: '',
  });

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Track conversion in Google Ads
    trackFormSubmission();
    
    // Mock form submission - will be replaced with backend integration
    alert('Thank you for your message! We will contact you soon.');
    setFormData({ name: '', phone: '', location: '', message: '' });
  };

  return (
    <div className="min-h-screen">
      <SEO
        title="Snake Catcher Number Bangalore | Call Snake Rescue 24/7"
        description="Snake catcher number in Bangalore - call Alhan +91 8310755854 or Mohan +91 9845080903 for urgent 24/7 snake rescue and removal near you."
        keywords={seoKeywords.contact}
        canonical="/contact"
        schemaData={[breadcrumbSchema([
          { name: 'Home', path: '/' },
          { name: 'Contact', path: '/contact' }
        ])]}
      />
      {/* Hero */}
      <section className="bg-gradient-to-b from-emerald-50 to-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6">
              <span className="text-emerald-600">Snake Catcher Number</span> Bangalore — Call 24/7
            </h1>
            <p className="text-lg text-gray-600 mb-8">
              Need immediate snake rescue? Call Alhan Nawaz (+91 8310755854) or Mohan K (+91 9845080903). For workshops or audits, fill out the form below.
            </p>
            <a href="tel:+918310755854">
              <Button size="lg" className="bg-emerald-600 hover:bg-emerald-700 text-white text-lg px-8 py-6">
                <Phone className="mr-2 h-5 w-5" />
                Emergency Call: +91 8310755854
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* Contact Cards & Form */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-6xl mx-auto">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
              {/* Contact Info */}
              <div>
                <h2 className="text-3xl font-bold text-gray-900 mb-6">Get in Touch</h2>
                <p className="text-gray-600 mb-8">
                  For emergencies, always call directly. For non-urgent inquiries about workshops or safety audits, you can use the contact form or reach out via phone.
                </p>
                
                <div className="space-y-6">
                  <Card className="border-emerald-100 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-lg mb-4 text-gray-900">Alhan Nawaz</h3>
                      <div className="space-y-3">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Phone className="h-6 w-6 text-emerald-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-700 mb-1">Phone</h4>
                            <a href="tel:+918310755854" className="text-emerald-600 hover:text-emerald-700 text-lg font-medium">
                              +91 8310755854
                            </a>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <MapPin className="h-6 w-6 text-emerald-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-700 mb-1">Location</h4>
                            <p className="text-gray-600">Babusapalya, Bangalore - 560043</p>
                            <p className="text-sm text-gray-500 mt-1">Serving Bangalore East</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-emerald-100 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <h3 className="font-semibold text-lg mb-4 text-gray-900">Mohan K</h3>
                      <div className="space-y-3">
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <Phone className="h-6 w-6 text-emerald-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-700 mb-1">Phone</h4>
                            <a href="tel:+919845080903" className="text-emerald-600 hover:text-emerald-700 text-lg font-medium">
                              +91 9845080903
                            </a>
                          </div>
                        </div>
                        <div className="flex items-start gap-4">
                          <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                            <MapPin className="h-6 w-6 text-emerald-600" />
                          </div>
                          <div>
                            <h4 className="font-medium text-gray-700 mb-1">Location</h4>
                            <p className="text-gray-600">Ulsoor, Bangalore - 560008</p>
                            <p className="text-sm text-gray-500 mt-1">Serving Central Bangalore</p>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card className="border-emerald-100 hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start gap-4">
                        <div className="w-12 h-12 bg-emerald-100 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Clock className="h-6 w-6 text-emerald-600" />
                        </div>
                        <div>
                          <h3 className="font-semibold text-lg mb-1">Availability</h3>
                          <p className="text-gray-700">24/7 Emergency Service</p>
                          <p className="text-sm text-gray-600 mt-1">We're always ready to help</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <Card className="border-emerald-200">
                  <CardContent className="p-6">
                    <h2 className="text-2xl font-bold text-gray-900 mb-6">Send Us a Message</h2>
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
                          Your Name *
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          required
                          value={formData.name}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="Enter your name"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                          Phone Number *
                        </label>
                        <input
                          type="tel"
                          id="phone"
                          name="phone"
                          required
                          value={formData.phone}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="Enter your phone number"
                        />
                      </div>

                      <div>
                        <label htmlFor="location" className="block text-sm font-medium text-gray-700 mb-1">
                          Location *
                        </label>
                        <input
                          type="text"
                          id="location"
                          name="location"
                          required
                          value={formData.location}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="Your area/locality"
                        />
                      </div>

                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                          Message *
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          required
                          value={formData.message}
                          onChange={handleChange}
                          rows="4"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500"
                          placeholder="Tell us about your inquiry (workshop, audit, etc.)"
                        />
                      </div>

                      <Button type="submit" className="w-full bg-emerald-600 hover:bg-emerald-700 text-white">
                        <Send className="mr-2 h-4 w-4" />
                        Send Message
                      </Button>
                    </form>

                    <p className="text-sm text-gray-500 mt-4 text-center">
                      For emergencies, please call directly instead of using this form.
                    </p>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-emerald-600 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Snake Emergency? Don't Wait!
          </h2>
          <p className="text-xl mb-8 text-emerald-50">
            Call now for immediate professional assistance
          </p>
          <a href="tel:+918310755854">
            <Button size="lg" className="bg-white text-emerald-600 hover:bg-gray-100 text-lg px-8 py-6">
              <Phone className="mr-2 h-5 w-5" />
              Call Now: +91 8310755854
            </Button>
          </a>
        </div>
      </section>
    </div>
  );
};

export default Contact;